import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase, supabasePublic } from '../lib/supabase';
import { Lock, ArrowRight, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';

// Capture URL tokens at module load — before detectSessionInUrl can clear them
const _hash = window.location.hash || '';
const _hashParams = new URLSearchParams(_hash.substring(1));
const _search = new URLSearchParams(window.location.search);
const _code = _search.get('code');
const _accessToken = _hashParams.get('access_token');
const _refreshToken = _hashParams.get('refresh_token');
// token_hash flow (not consumed by email prefetch — scanners can't run verifyOtp)
const _tokenHash = _search.get('token_hash');
const _type = _search.get('type');

function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [expired, setExpired] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Check for errors in hash (implicit flow)
    if (_hashParams.get('error_code') === 'otp_expired' ||
        _hashParams.get('error') === 'access_denied' ||
        _hash.includes('error_code=otp_expired') ||
        _hash.includes('error=access_denied')) {
      setExpired(true);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' && !cancelled) {
        setSessionReady(true);
      }
    });

    const establishSession = async () => {
      // 0. token_hash flow (current emails): verify the hash to mint a recovery session.
      // A GET prefetch by an email scanner can't run this, so the token survives until the user clicks.
      if (_tokenHash) {
        const { error } = await supabase.auth.verifyOtp({
          type: _type || 'recovery',
          token_hash: _tokenHash,
        });
        if (!error && !cancelled) { setSessionReady(true); return; }
        if (error && !cancelled) { setExpired(true); return; }
      }

      // 1. Session may already exist (detectSessionInUrl processed fast)
      const { data: { session } } = await supabase.auth.getSession();
      if (session && !cancelled) { setSessionReady(true); return; }

      // 2. Implicit flow: we have tokens in the hash — set session directly
      if (_accessToken && _refreshToken) {
        const { error } = await supabase.auth.setSession({
          access_token: _accessToken,
          refresh_token: _refreshToken,
        });
        if (!error && !cancelled) { setSessionReady(true); return; }
      }

      // 3. PKCE flow (old emails): try exchanging the code directly
      if (_code) {
        try {
          const { error } = await supabase.auth.exchangeCodeForSession(_code);
          if (!error && !cancelled) { setSessionReady(true); return; }
        } catch {}
        // Code may have been consumed by detectSessionInUrl — check session
        const { data: { session: s2 } } = await supabase.auth.getSession();
        if (s2 && !cancelled) { setSessionReady(true); return; }
      }

      // 4. Brief fallback poll (detectSessionInUrl may still be finishing)
      for (let i = 0; i < 6; i++) {
        await new Promise(r => setTimeout(r, 500));
        if (cancelled) return;
        const { data: { session: s } } = await supabase.auth.getSession();
        if (s && !cancelled) { setSessionReady(true); return; }
      }

      // Nothing worked after ~3 seconds of polling
      if (!cancelled) setExpired(true);
    };

    establishSession();

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        if (updateError.message?.includes('expired') || updateError.message?.includes('otp')) {
          setExpired(true);
        } else {
          setError(updateError.message);
        }
        return;
      }
      setSuccess(true);
      await supabase.auth.signOut();
      setTimeout(() => navigate('/auth', { replace: true }), 2500);
    } catch (err) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  if (expired) {
    const handleResend = async (e) => {
      e.preventDefault();
      if (!resendEmail) { setError('Please enter your email'); return; }
      setResendLoading(true);
      setError('');
      try {
        const { error: resetErr } = await supabasePublic.auth.resetPasswordForEmail(resendEmail, {
          redirectTo: window.location.origin + '/reset-password'
        });
        if (resetErr) throw resetErr;
        setResendSent(true);
      } catch (err) {
        setError(err.message || 'Failed to send reset email');
      } finally {
        setResendLoading(false);
      }
    };

    return (
      <div className="min-h-screen bg-dive-black flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-dive-dark border border-dive-gray rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-display text-white mb-3">Link Expired</h1>
          <p className="text-gray-400 mb-6 text-sm">
            This password reset link has expired or is invalid. Enter your email below to get a new one.
          </p>
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}
          {resendSent ? (
            <div className="bg-green-500/10 border border-green-500/30 text-green-400 p-4 rounded-lg text-sm">
              <CheckCircle className="w-5 h-5 inline mr-2" />
              Reset email sent! Check your inbox and click the new link.
            </div>
          ) : (
            <form onSubmit={handleResend} className="space-y-3">
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full text-center"
                required
              />
              <button
                type="submit"
                disabled={resendLoading}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {resendLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send New Reset Link'}
              </button>
            </form>
          )}
          <Link to="/auth" className="inline-block mt-4 text-gray-500 text-sm underline">
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-dive-black flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-dive-dark border border-dive-gray rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-display text-white mb-3">Password Updated!</h1>
          <p className="text-gray-400">Redirecting you to sign in...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dive-black flex items-start justify-center p-4 overflow-y-auto">
      <div className="w-full max-w-md my-auto">
        <div className="bg-dive-dark border border-dive-gray rounded-2xl p-8">
          <div className="text-center mb-6">
            <img src="/logo-full.png" alt="Tilted" className="w-40 mx-auto mb-4" />
            <h1 className="text-2xl font-display text-white">Set New Password</h1>
            <p className="text-gray-400 text-sm mt-1">Enter your new password below</p>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-gray-400 text-sm mb-1 block">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11"
                  required
                  minLength={6}
                  disabled={!sessionReady}
                />
              </div>
            </div>

            <div>
              <label className="text-gray-400 text-sm mb-1 block">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11"
                  required
                  minLength={6}
                  disabled={!sessionReady}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !sessionReady}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : !sessionReady ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying link...
                </>
              ) : (
                <>
                  Update Password
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
